import { type CryptoCurrency, type FiatCurrency, rest } from '@0xtorch/core'
import { z } from 'zod'
import type { AssetBalance } from '../../types'
import { createSha256Hash } from '../../utils/createSha256Hash'
import { getAsset } from '../../utils/getAsset'
import {
  binanceCoinFutureApiEndpoint,
  binanceSymbolCryptoIdMap,
} from '../constants'
import { futureAssetSchema } from './schema'

const responseSchema = z.array(futureAssetSchema)

const urlPath = '/dapi/v1/balance'

type FetchCoinFutureAccountAssetsParameters = {
  apiKey: string
  apiSecret: string
  proxy?: string
  proxyHeaders?: Record<string, string>
  cryptoCurrencies: readonly CryptoCurrency[]
  fiatCurrencies: readonly FiatCurrency[]
}

export const fetchCoinFutureAccountAssets = async ({
  apiKey,
  apiSecret,
  proxy,
  proxyHeaders,
  cryptoCurrencies,
  fiatCurrencies,
}: FetchCoinFutureAccountAssetsParameters): Promise<
  readonly AssetBalance[]
> => {
  const timestamp = Date.now()

  const url = new URL(urlPath, binanceCoinFutureApiEndpoint)
  url.searchParams.append('timestamp', timestamp.toString())
  url.searchParams.append(
    'signature',
    await createSha256Hash(url.searchParams.toString(), apiSecret),
  )

  const response = await rest(
    proxy === undefined
      ? url.toString()
      : `${proxy}${encodeURIComponent(url.toString())}`,
    {
      fetchOptions: {
        method: 'GET',
        headers: {
          'X-MBX-APIKEY': apiKey,
          ...proxyHeaders,
        },
      },
      schema: responseSchema,
    },
  )

  const assetBalances: AssetBalance[] = []
  for (const assetData of response) {
    const asset = getAsset({
      symbol: assetData.asset,
      cryptoCurrencies,
      fiatCurrencies,
      symbolCryptoIdMap: binanceSymbolCryptoIdMap,
    })
    if (asset === undefined) {
      continue
    }
    if (assetData.crossWalletBalance.value > 0n) {
      assetBalances.push({
        key: 'binance_coin-future-balance',
        cryptoId: asset.type === 'CryptoCurrency' ? asset.id : undefined,
        fiatId: asset.type === 'FiatCurrency' ? asset.id : undefined,
        amount: assetData.crossWalletBalance,
        timestamp,
      })
    }
    if (assetData.crossUnPnl.value !== 0n) {
      assetBalances.push({
        key: 'binance_coin-future-unrealized-pnl',
        cryptoId: asset.type === 'CryptoCurrency' ? asset.id : undefined,
        fiatId: asset.type === 'FiatCurrency' ? asset.id : undefined,
        amount: assetData.crossUnPnl,
        timestamp,
      })
    }
  }

  return assetBalances
}
